/**
 * ComplimentaryAccountDialog – Superadmin-Tool: legt einen vollwertigen
 * Complimentary-Account an (wie normale Registrierung, aber kostenlos, ohne
 * Stripe, höchstes Paket professional_ai, alle Module, unbegrenzt).
 * Enthält Trigger-Button + Dialog. Einbindbar in jede Superadmin-Seite.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Gift, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL_FORMS = [
  { value: 'einzelunternehmen', label: 'Einzelunternehmen' },
  { value: 'gbr', label: 'GbR (Gesellschaft bürgerlichen Rechts)' },
  { value: 'ug', label: 'UG (haftungsbeschränkt)' },
  { value: 'gmbh', label: 'GmbH' },
  { value: 'ag', label: 'AG' },
  { value: 'kg', label: 'KG (Kommanditgesellschaft)' },
  { value: 'ohg', label: 'OHG (Offene Handelsgesellschaft)' },
];
const INDUSTRIES = [
  { value: 'tankstelle', label: 'Tankstelle' },
  { value: 'einzelhandel', label: 'Einzelhandel' },
  { value: 'gastronomie', label: 'Gastronomie' },
  { value: 'andere', label: 'Andere' },
];
const BRANDS = ['JET', 'Shell', 'Aral', 'Esso', 'TotalEnergies', 'OMV', 'AVIA',
  'Agip/Eni', 'HEM', 'Star/ORLEN', 'Raiffeisen', 'Westfalen', 'bft', 'Freie Tankstelle', 'Sonstige'];

const EMPTY = {
  first_name: '', last_name: '', company_name: '', legal_form: '', industry: 'tankstelle',
  brand: '', email: '', password: '', street: '', house_number: '', postal_code: '', city: '',
  location_name: '', station_number: '',
};

export function ComplimentaryAccountDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const setC = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const close = () => { setOpen(false); setForm(EMPTY); setResult(null); };

  const submit = async (replaceExisting = false) => {
    if (!form.first_name || !form.last_name || !form.company_name ||
        !form.legal_form || !form.email || !form.password) {
      toast.error('Bitte Pflichtfelder ausfüllen (Vorname, Nachname, Firma, Rechtsform, E-Mail, Passwort)');
      return;
    }
    if (form.industry === 'tankstelle' && !form.brand) {
      toast.error('Bitte Gesellschafter / Marke auswählen');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/superadmin/customers/create-complimentary`, {
        ...form, replace_existing: replaceExisting,
      });
      setResult(res.data);
      toast.success('Complimentary-Account angelegt');
      onCreated?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'email_exists') {
        if (d.is_protected) {
          toast.error(`E-Mail bereits vergeben – Account ist ${d.existing_status || 'aktiv'} und geschützt (wird nicht ersetzt).`);
        } else if (window.confirm(
          `E-Mail bereits vergeben (Status: ${d.existing_status || 'unbekannt'}). ` +
          `Vermutlich ein hängender/unbezahlter Account (z.B. abgebrochener Checkout). ` +
          `Alten Account löschen und sauber neu als Complimentary anlegen?`
        )) {
          setSaving(false);
          return submit(true);
        }
      } else {
        toast.error(typeof d === 'string' ? d : 'Fehler beim Anlegen des Accounts');
      }
    } finally {
      setSaving(false);
    }
  };

  const field = 'space-y-1';

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
        data-testid="open-complimentary-dialog-btn"
      >
        <Gift className="h-4 w-4" />
        Complimentary-Account
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="complimentary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-emerald-600" />
              Complimentary-Account anlegen
            </DialogTitle>
            <DialogDescription>
              Vollwertiger Account wie bei der normalen Registrierung – kostenlos, ohne Stripe,
              höchstes Paket (Professional AI), alle Module, zeitlich unbegrenzt.
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="space-y-3 py-2" data-testid="complimentary-result">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                <CheckCircle className="h-5 w-5" /> Account erfolgreich angelegt
              </div>
              <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1">
                <div><span className="text-muted-foreground">Kundennummer:</span> <strong>{result.customer_number}</strong></div>
                <div><span className="text-muted-foreground">Admin-Login:</span> <strong>{result.admin_email}</strong></div>
                <div><span className="text-muted-foreground">Freigeschaltete Module:</span> <strong>{result.modules_enabled}</strong></div>
                {result.replaced_existing && <div className="text-amber-600">Hinweis: Ein hängender Vorgänger-Account mit dieser E-Mail wurde ersetzt.</div>}
              </div>
              <p className="text-xs text-muted-foreground">
                Der Mitarbeiter-Login wird anschließend im Account unter <strong>Personal → Mitarbeiter hinzufügen</strong> angelegt.
              </p>
              <DialogFooter>
                <Button onClick={close} data-testid="complimentary-done-btn">Fertig</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-1">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schritt 1 · Firma & Login</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className={field}>
                    <Label>Vorname *</Label>
                    <Input value={form.first_name} onChange={e => setC('first_name', e.target.value)} data-testid="comp-first-name" />
                  </div>
                  <div className={field}>
                    <Label>Nachname *</Label>
                    <Input value={form.last_name} onChange={e => setC('last_name', e.target.value)} data-testid="comp-last-name" />
                  </div>
                  <div className={`${field} col-span-2`}>
                    <Label>Firmenname *</Label>
                    <Input value={form.company_name} onChange={e => setC('company_name', e.target.value)} placeholder="z.B. Mustermann Tankstellen GmbH" data-testid="comp-company" />
                  </div>
                  <div className={field}>
                    <Label>Rechtsform *</Label>
                    <Select value={form.legal_form} onValueChange={v => setC('legal_form', v)}>
                      <SelectTrigger data-testid="comp-legal-form"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                      <SelectContent>
                        {LEGAL_FORMS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={field}>
                    <Label>Branche *</Label>
                    <Select value={form.industry} onValueChange={v => setC('industry', v)}>
                      <SelectTrigger data-testid="comp-industry"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.industry === 'tankstelle' && (
                  <div className="mt-3 space-y-1">
                    <Label>Gesellschafter / Marke *</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {BRANDS.map(b => (
                        <button
                          key={b} type="button" onClick={() => setC('brand', b === 'Sonstige' ? '' : b)}
                          data-testid={`comp-brand-${b}`}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                            form.brand === b ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-background border-border hover:border-emerald-400'
                          }`}
                        >{b}</button>
                      ))}
                    </div>
                    <Input value={form.brand} onChange={e => setC('brand', e.target.value)} placeholder="Marke (frei eingebbar bei 'Sonstige')" className="mt-1.5" data-testid="comp-brand-input" />
                    <p className="text-[11px] text-muted-foreground">Beeinflusst die automatischen Produktvorschläge.</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className={field}>
                    <Label>E-Mail *</Label>
                    <Input type="email" value={form.email} onChange={e => setC('email', e.target.value)} data-testid="comp-email" />
                  </div>
                  <div className={field}>
                    <Label>Passwort *</Label>
                    <Input value={form.password} onChange={e => setC('password', e.target.value)} data-testid="comp-password" />
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schritt 2 · Standort</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className={field}>
                    <Label>Straße</Label>
                    <Input value={form.street} onChange={e => setC('street', e.target.value)} data-testid="comp-street" />
                  </div>
                  <div className={field}>
                    <Label>Nr.</Label>
                    <Input value={form.house_number} onChange={e => setC('house_number', e.target.value)} data-testid="comp-house" />
                  </div>
                  <div className={field}>
                    <Label>PLZ</Label>
                    <Input value={form.postal_code} onChange={e => setC('postal_code', e.target.value)} data-testid="comp-plz" />
                  </div>
                  <div className={field}>
                    <Label>Stadt</Label>
                    <Input value={form.city} onChange={e => setC('city', e.target.value)} data-testid="comp-city" />
                  </div>
                  <div className={field}>
                    <Label>Standortname</Label>
                    <Input value={form.location_name} onChange={e => setC('location_name', e.target.value)} placeholder="z.B. JET Hamburg City" data-testid="comp-locname" />
                  </div>
                  <div className={field}>
                    <Label>Stationsnummer (optional)</Label>
                    <Input value={form.station_number} onChange={e => setC('station_number', e.target.value)} data-testid="comp-station" />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={close} disabled={saving}>Abbrechen</Button>
                <Button onClick={() => submit(false)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="comp-submit-btn">
                  {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Wird angelegt…</> : 'Account anlegen'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ComplimentaryAccountDialog;
